import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  styled,
  SelectChangeEvent,
  Pagination,
} from '@mui/material';
import { fetchSystemJobs, fetchSystemJobsCount } from '../api/features/systemJob/systemJobApi';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY, SAP_SHADOWS } from '../theme/sapDesignSystem';
import { PageLayout } from '../components';
import { logger } from '../utils/logger';
import { epochToDateTime } from '../api/utils';

// SAP Fiori System Job Components - aligned with Job Details list

const SAPDataCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
}));

const SAPTableHeader = styled(TableHead)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapLightBlue,
  '& .MuiTableCell-head': {
    fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    color: SAP_COLORS.sapGray6,
    borderBottom: `2px solid ${SAP_COLORS.sapBlue}`,
  },
}));

export default function SystemJobDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientCode } = useClientCode();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch system job details - load by default on mount and when page/rpp changes
  const fetchData = useCallback(async (pageNumber: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const [data, count] = await Promise.all([
        fetchSystemJobs({
          clientCode,
          firstResult: pageNumber * pageSize,
          maxResult: pageSize,
        }),
        fetchSystemJobsCount({ clientCode })
      ]);
      
      logger.log('System Job API Response:', { data, count, countType: typeof count });
      logger.log('First row data:', data[0]); // Debug individual row structure
      
      setRows(data);
      setTotalCount(count || 0);
    } catch (err: any) {
      setError('Failed to load system job details');
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [clientCode]);

  // Load data on mount and when page or rowsPerPage changes
  useEffect(() => {
    fetchData(currentPage, rowsPerPage);
  }, [fetchData, currentPage, rowsPerPage]);

  // Handle page change
  const handlePageChange = (page: number, newRowsPerPage: number) => {
    setCurrentPage(page);
    setRowsPerPage(newRowsPerPage);
    fetchData(page, newRowsPerPage);
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    const newRpp = Number(event.target.value) || 10;
    setRowsPerPage(newRpp);
    setCurrentPage(0);
    fetchData(0, newRpp);
  };

  const formatDate = (dateValue: string | number) => {
    if (!dateValue) return 'N/A';
    return epochToDateTime(dateValue, true) || 'N/A';
  };

  const handleRowClick = useCallback(
    (row: any) => {
      if (row?.id == null) return;
      navigate(`/system-job/${row.id}`, {
        state: { fromListSearch: location.pathname + location.search },
      });
    },
    [navigate, location.pathname, location.search]
  );

  return (
    <PageLayout
      title="System Jobs"
      description="Search and manage system jobs for monitoring and control"
      titleSx={{ color: SAP_COLORS.sapBlue }}
    >
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: '8px',
            backgroundColor: SAP_COLORS.sapRed + '10',
            color: SAP_COLORS.sapRed,
            border: `1px solid ${SAP_COLORS.sapRed}30`,
          }}
        >
          {error}
        </Alert>
      )}

      <SAPDataCard>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <CircularProgress sx={{ color: SAP_COLORS.sapBlue }} size={48} />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 0 }}>
                <Table sx={{ minWidth: 650 }} stickyHeader aria-label="system job table">
                  <SAPTableHeader>
                    <TableRow>
                      <TableCell sx={{ width: 70 }}>S.No.</TableCell>
                      <TableCell>Job Name</TableCell>
                      <TableCell>Last Start Time</TableCell>
                      <TableCell>Last End Time</TableCell>
                      <TableCell>Next Run Time</TableCell>
                      <TableCell align="right">Interval</TableCell>
                    </TableRow>
                  </SAPTableHeader>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ color: SAP_COLORS.sapGray5, py: SAP_SPACING.xl, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium }}>
                          No system jobs found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row, idx) => (
                        <TableRow
                          key={`systemjob-${row.id ?? currentPage * rowsPerPage + idx + 1}-${row.jobName ?? idx}`}
                          onClick={() => handleRowClick(row)}
                          sx={{
                            bgcolor: idx % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                            '&:hover': { bgcolor: SAP_COLORS.sapLightBlue + '30', cursor: 'pointer' },
                            transition: 'background-color 0.2s ease',
                          }}
                        >
                          <TableCell sx={{ color: SAP_COLORS.sapGray6, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium }}>
                            {currentPage * rowsPerPage + idx + 1}
                          </TableCell>
                          <TableCell sx={{ color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium }}>
                            {row.jobName || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ color: SAP_COLORS.sapGray6 }}>{formatDate(row.lastStartTime)}</TableCell>
                          <TableCell sx={{ color: SAP_COLORS.sapGray6 }}>{formatDate(row.lastEndTime)}</TableCell>
                          <TableCell sx={{ color: SAP_COLORS.sapGray6 }}>{formatDate(row.nextRunTime)}</TableCell>
                          <TableCell align="right" sx={{ color: SAP_COLORS.sapGray6 }}>{row.interval ?? 'N/A'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {rows.length > 0 && (
                <Box sx={{ mt: 2, px: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {(currentPage * rowsPerPage) + 1}-{Math.min((currentPage * rowsPerPage) + rows.length, totalCount)} of {totalCount} system jobs
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Rows per page</InputLabel>
                      <Select value={rowsPerPage} onChange={handleRowsPerPageChange} label="Rows per page">
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                    <Pagination
                      count={Math.ceil(totalCount / rowsPerPage)}
                      page={currentPage + 1}
                      onChange={(_, newPage) => handlePageChange(newPage - 1, rowsPerPage)}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </SAPDataCard>
    </PageLayout>
  );
} 